import React, {FC, useEffect, useRef, useState} from "react";
import cls from "../OhmEnergy/Products/product.module.scss"
import {AnnualElectricity, Capacity, OperationDate} from "../../../svg";
import {IProduct} from "../../../types/homePageTypes";
import NewPhotoSwipe from "./NewPhotoSwipe";
import {domainUrl} from "../../../helper";

interface IProps {
    product: IProduct
    dbName: string
}

const getYouTubeEmbedUrl = (url: string): string | null => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|watch\?.+&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? `https://www.youtube.com/embed/${match[2]}` : null;
};

const YouTubeEmbed = ({ url }: { url: string }) => {
    const embedUrl = getYouTubeEmbedUrl(url);
    if (!embedUrl) return <p>Invalid YouTube URL</p>;
    return <div className={cls["inner-iframe-wrapper"]}>
            <iframe
                src={embedUrl}
                className={cls["inner-embed-iframe"]}
                allowFullScreen
                allow="accelerometer *; clipboard-write *; encrypted-media *; gyroscope *; picture-in-picture *; web-share *;"
            />
        <div className={`${cls["iframe-gradient-part-all"]} ${cls["iframe-gradient-top"]}`}/>
        <div className={`${cls["iframe-gradient-part-all"]} ${cls["iframe-gradient-bottom"]}`}/>
    </div>
}

const CustomOhmVirtual: FC<IProps> = ({product, dbName}) => {
    const {
        flats: [{
            name: productName = "",
            short_description: productShortDesc = "",
        } = {}] = [],
        details = []
    } = product || {};

    const attributeGetter = (code: string) => {
        return details?.find(elem => elem?.attribute?.[0]?.code === code);
    }
    const location = attributeGetter("location");
    const capacity = attributeGetter("capacity");
    const electricity = attributeGetter("annual_electricity_production");
    const commercial = attributeGetter("commercial_operation_date");
    const solar_modules = attributeGetter("solar_modules");
    const inverter = attributeGetter("inverter");
    const youTubeEmbed = attributeGetter("youtube_video_link");
    const [isOpen, setIsOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [galleryKey, setGalleryKey] = useState(0);
    const galleryRef = useRef<any>(null);
    const openGallery = (index: number) => {
        setIsOpen(true);
        setCurrentIndex(index);
        setGalleryKey(prev => prev + 1);
    };

    // useEffect(() => {
    //     const initializeGallery = async () => {
    //         if (!isOpen || galleryRef.current) return;
    //
    //         const options = {
    //             index: currentIndex,
    //             bgOpacity: 0.9,
    //             history: false
    //         };
    //
    //         const items = (product?.images || [])
    //             .filter(image => !!image) // prevent null/undefined
    //             .map((image) => ({
    //                 src: domainUrl(`${dbName}/${image}`),
    //                 msrc: domainUrl(`${dbName}/${image}`),
    //                 w: 1000,
    //                 h: 1000,
    //             }));
    //
    //         if (!items.length) return; // nothing to show — prevent init
    //
    //         const { default: PhotoSwipe } = await import("photoswipe");
    //         const { default: PhotoSwipeUI_Default } = await import("photoswipe/dist/photoswipe-ui-default");
    //
    //         const pswpElement = document.querySelector('.pswp');
    //         if (!pswpElement) return; // PSWP DOM not ready
    //
    //         const gallery = new PhotoSwipe(pswpElement as any, PhotoSwipeUI_Default, items, options);
    //
    //         gallery.listen('beforeChange', () => {
    //             const index = gallery.getCurrentIndex();
    //             setCurrentIndex(index);
    //         });
    //
    //         gallery.listen('close', () => {
    //             setIsOpen(false);
    //             galleryRef.current = null;
    //         });
    //
    //         gallery.init();
    //         galleryRef.current = gallery;
    //     };
    //     initializeGallery()
    //     return () => {
    //         // cleanup on unmount
    //         if (galleryRef.current) {
    //             galleryRef.current.destroy();
    //             galleryRef.current = null;
    //         }
    //     };
    // }, [isOpen, galleryKey]);

    useEffect(() => {
        const initializeGallery = async () => {
            if (!isOpen) return;

            if (galleryRef.current) {
                galleryRef.current.destroy();
                galleryRef.current = null;
            }

            const options = {
                index: currentIndex,
                bgOpacity: 0.9,
                history: false
            };

            const items = product?.images.map((image) => ({
                src: domainUrl(`${dbName}/${image}`),
                msrc: domainUrl(`${dbName}/${image}`),
                w: 1000,
                h: 1000,
            }));

            const { default: PhotoSwipe } = await import("photoswipe");
            const { default: PhotoSwipeUI_Default } = await import("photoswipe/dist/photoswipe-ui-default");

            const gallery = new PhotoSwipe(document.querySelector('.pswp') as HTMLElement, PhotoSwipeUI_Default, items, options);

            gallery.listen('beforeChange', () => {
                const index = gallery.getCurrentIndex();
                setCurrentIndex(index);
            });

            gallery.listen('close', () => {
                setIsOpen(false);
                galleryRef.current = null; // cleanup here
            });

            gallery.init();
            galleryRef.current = gallery;
        };

        initializeGallery();

        return () => {
            if (galleryRef.current) {
                galleryRef.current.destroy();
                galleryRef.current = null;
            }
        };
    }, [isOpen, galleryKey]);

    return (
        <div className="block">
            <NewPhotoSwipe/>
            <div className={cls["inner-page-info-wrapper"]}>
                <h1 className={`${cls["inner-heading"]} container`}>{productName}</h1>
                {location && <p className={cls["inner-location"]}>{location?.attribute_value}</p>}
                <div className={cls["inner-product-info"]}>
                    <div className={cls["inner-product-short"]} dangerouslySetInnerHTML={{__html: productShortDesc}}/>
                    <div className={cls["info-product-wrap"]} style={{flexDirection: "row"}}>
                        {capacity && <div className={cls["each-info-product"]}>
                            <Capacity/> <p><b>{capacity?.attribute_value}</b></p>
                        </div>}
                        {electricity && <div className={cls["each-info-product"]}>
                            <AnnualElectricity/> <p><b>{electricity?.attribute_value}</b></p>
                        </div>}
                        {commercial && <div className={cls["each-info-product"]}>
                            <OperationDate/> <p><b>{commercial?.attribute_value}</b></p>
                        </div>}
                    </div>
                    <div className={cls["inner-bottom-attributes"]}>
                        {inverter && <div className={cls["each-bottom-attribute"]}>
                            <span><b>{inverter?.attribute_name}: </b></span>
                            <span> {inverter?.attribute_value}</span>
                        </div>}
                        {solar_modules && <div className={cls["each-bottom-attribute"]}>
                            <span><b>{solar_modules?.attribute_name}: </b></span>
                            <span> {solar_modules?.attribute_value}</span>
                        </div>}
                    </div>
                </div>
            </div>
            {youTubeEmbed && <YouTubeEmbed url={youTubeEmbed?.attribute_value} />}
            <div className={cls["inner-image-box-wrapper"]}>
                <div className={cls["inner-image-box"]}>
                    {product?.images.map((elem, index) => (
                        <div
                            key={index}
                            onClick={() => openGallery(index)}
                            className={cls["inner-each-image"]}
                        >
                            <img src={elem} alt={`Product Image ${index}`}/>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default CustomOhmVirtual;